import { useRef } from "react";
import { ChevronRightIcon, PaperclipIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import type { Obligatoriedad } from "@/lib/informes";
import { cn } from "@/lib/utils";

import { ArchivosDelDocumento, EXTENSIONES_ACEPTADAS, MarcaDeOrigen } from "./ArchivosDelDocumento";
import type { Clasificacion } from "./SelectorDeClasificacion";
import {
  origenesDeLosArchivos,
  subtituloDelDocumento,
  type EstadoDelDocumento,
  type FilaDeDocumento,
} from "./documentosDeLaPestana";

/** Lo que dice cada opción del conmutador. */
const ETIQUETA_OBLIGATORIEDAD: Record<Obligatoriedad, string> = {
  obligatorio: "Obligatorio",
  opcional: "Opcional",
  no_aplica: "No aplica",
};

/** El punto de estado: lleno y de color si hay algo que decir, punteado si no. */
const PUNTO: Record<EstadoDelDocumento, string> = {
  ok: "border-success-dot bg-success-dot",
  falta: "border-warning-dot bg-warning-dot",
  opc: "border-dashed border-ink-ghost bg-transparent",
  na: "border-dashed border-ink-ghost bg-transparent",
};

interface FilaDelDocumentoProps {
  fila: FilaDeDocumento;
  abierta: boolean;
  onAlternar: () => void;
  onElegir: (valor: Obligatoriedad) => void;
  urlDeDescarga: (archivoId: number) => string;
  /** Hay una subida o un borrado en curso en ESTE documento. */
  ocupado: boolean;
  /** Los rechazos del navegador y del servidor, una línea por archivo. */
  error: string | null;
  onAdjuntar: (archivos: File[]) => void;
  onQuitar: (archivoId: number) => void;
  onCorregirFecha: (archivoId: number, fecha: string | null) => void;
  onAnotar: (archivoId: number, nota: string | null) => void;
  onReprocesar: (archivoId: number) => void;
  archivoReprocesando: number | null;
  /** Para cambiar el tipo de un archivo; sin esto la fila no lo ofrece. */
  clasificacion?: Clasificacion;
}

/**
 * Un documento del catálogo: su estado, su conmutador de obligatoriedad, el
 * botón de adjuntar y —desplegados— sus archivos.
 *
 * EL SELECTOR DE ARCHIVOS VIVE ACÁ y no en la lista de abajo, porque "Adjuntar"
 * tiene que funcionar con la fila plegada, que es como arranca.
 *
 * TOCAR LA FILA DESPLIEGA, pero el conmutador y "Adjuntar" no: son controles
 * con su propia acción y desplegar al apretarlos movería la lista bajo el dedo.
 * El chevron es un botón de verdad para quien navega con teclado; el clic sobre
 * la fila es solo una comodidad.
 */
export function FilaDelDocumento({
  fila,
  abierta,
  onAlternar,
  onElegir,
  urlDeDescarga,
  ocupado,
  error,
  onAdjuntar,
  onQuitar,
  onCorregirFecha,
  onAnotar,
  onReprocesar,
  archivoReprocesando,
  clasificacion,
}: FilaDelDocumentoProps) {
  const input = useRef<HTMLInputElement>(null);
  const { documento, archivos, estado, obligatoriedad } = fila;
  const cantidad = archivos.length;
  const desplegable = cantidad > 0;
  const nombreDeAdjuntar = `Adjuntar archivo a ${documento.nombre}`;

  return (
    <div className={cn("border-t first:border-t-0", estado === "na" && "opacity-60")}>
      <div
        onClick={desplegable ? onAlternar : undefined}
        className={cn(
          "hover:bg-muted/40 flex min-h-11 items-center gap-3 py-2.5 pr-4 pl-5",
          desplegable && "cursor-pointer",
        )}
      >
        <span
          aria-hidden="true"
          className={cn("size-[0.5625rem] flex-none rounded-full border-[1.5px]", PUNTO[estado])}
        />

        <div className="min-w-0 flex-1">
          <div
            className={cn(
              "truncate text-[0.84375rem] font-bold",
              estado === "na" ? "text-ink-muted" : "text-ink",
            )}
          >
            {documento.nombre}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2">
            <span
              className={cn(
                "text-[0.71875rem]",
                estado === "falta"
                  ? "text-warning-soft-foreground font-bold"
                  : "text-ink-muted font-semibold",
              )}
            >
              {subtituloDelDocumento(estado, cantidad)}
            </span>
            {estado === "na"
              ? null
              : origenesDeLosArchivos(archivos).map((origen) => (
                  <MarcaDeOrigen key={origen} origen={origen} />
                ))}
          </div>
        </div>

        {/* Los controles no despliegan la fila. */}
        <div
          role="group"
          aria-label={`Obligatoriedad de ${documento.nombre}`}
          onClick={(evento) => evento.stopPropagation()}
          className="bg-muted flex flex-none gap-0.5 rounded-[0.5625rem] p-0.5"
        >
          {(["obligatorio", "opcional", "no_aplica"] as const).map((opcion) => (
            <button
              key={opcion}
              type="button"
              aria-pressed={obligatoriedad === opcion}
              onClick={() => onElegir(opcion)}
              className={cn(
                "inline-flex h-6.5 items-center rounded-lg px-2.5 text-[0.6875rem] whitespace-nowrap transition-colors",
                obligatoriedad === opcion
                  ? "bg-card text-ink font-bold shadow-sm"
                  : "text-ink-muted hover:text-ink font-semibold",
              )}
            >
              {ETIQUETA_OBLIGATORIEDAD[opcion]}
            </button>
          ))}
        </div>

        {/* El input va oculto y lo dispara el botón: el control nativo no se
            puede estilar y no acepta el DS. `sr-only` y no `display:none` para
            que siga existiendo para la tecnología asistiva. */}
        <input
          ref={input}
          type="file"
          multiple
          accept={EXTENSIONES_ACEPTADAS}
          className="sr-only"
          aria-label={nombreDeAdjuntar}
          onChange={(evento) => {
            // Se copia ANTES de limpiar el input: `files` es una lista viva y
            // vaciar el valor la deja en cero.
            const elegidos = Array.from(evento.target.files ?? []);
            // Se limpia SIEMPRE: sin esto, volver a elegir el mismo archivo no
            // dispara `change`, y reintentar tras un error del servidor sería
            // imposible sin elegir otro primero.
            evento.target.value = "";
            if (elegidos.length > 0) onAdjuntar(elegidos);
          }}
        />

        {/* "No aplica" no recibe archivos: se esconde en vez de apagarse, porque
            un botón apagado sin explicación no dice por qué. Oscuro mientras
            falte un obligatorio —es lo que hay que hacer—, de contorno si no. */}
        {estado === "na" ? null : (
          <Button
            type="button"
            size="sm"
            variant={estado === "falta" ? "secondary" : "outline"}
            disabled={ocupado}
            aria-label={nombreDeAdjuntar}
            title="Adjuntar archivo"
            onClick={(evento) => {
              evento.stopPropagation();
              input.current?.click();
            }}
          >
            {ocupado ? <Spinner /> : <PaperclipIcon aria-hidden="true" />}
            Adjuntar
          </Button>
        )}

        {desplegable ? (
          <button
            type="button"
            aria-expanded={abierta}
            aria-label={`${abierta ? "Contraer" : "Ver"} archivos de ${documento.nombre}`}
            onClick={(evento) => {
              evento.stopPropagation();
              onAlternar();
            }}
            className="text-ink-hint flex-none rounded-md p-0.5"
          >
            <ChevronRightIcon
              className={cn("size-3.5 transition-transform", abierta && "rotate-90")}
              aria-hidden="true"
            />
          </button>
        ) : (
          <span className="size-4.5 flex-none" aria-hidden="true" />
        )}
      </div>

      {/* Una línea por archivo rechazado, "nombre: motivo": con un lote, el
          motivo sin el nombre no dice CUÁL de los archivos falló. Va fuera de la
          parte plegable para que se vea con la fila cerrada. */}
      {error ? (
        <div className="pr-4 pb-2 pl-10">
          {error.split("\n").map((linea, i) => (
            <p key={`${i}-${linea}`} className="text-destructive text-xs">
              {linea}
            </p>
          ))}
        </div>
      ) : null}

      {abierta && desplegable ? (
        <ArchivosDelDocumento
          archivos={archivos}
          urlDeDescarga={urlDeDescarga}
          ocupado={ocupado}
          onAbrirSelector={() => input.current?.click()}
          onSoltar={onAdjuntar}
          onQuitar={onQuitar}
          onCorregirFecha={onCorregirFecha}
          onAnotar={onAnotar}
          onReprocesar={onReprocesar}
          archivoReprocesando={archivoReprocesando}
          clasificacion={clasificacion}
        />
      ) : null}
    </div>
  );
}
